<template>
  <section
    ref="trigger"
    class="page-hero"
    :class="!data.section_anchor ? 'no-button' : ''">
    <div
      v-if="data.update_color"
      class="background"
      :style="{ backgroundColor: data.bottom_color }"></div>

    <div class="container">
      <div class="wrapper">
        <div class="hero-content-wrapper">
          <div class="hero-content">
            <div
              :id="headlineId"
              ref="headline"
              class="headline"
              :class="data.big_title ? 'big-title' : ''"
              v-html="data.page_hero_headline"></div>

            <div
              v-if="data.show_button"
              :id="buttonId"
              class="button-area inline-block w-fit">
              <ArrowCircleButton
                :link="data.button"
                :arrow-direction="data.arrow_down ? 'down' : ''" />
            </div>
          </div>
        </div>

        <div class="hero-image">
          <img
            v-show="heroLoaded"
            ref="pageHeroImg"
            fetchpriority="high"
            :src="data.page_hero_image.url"
            :alt="data.page_hero_image.alt"
            :title="data.page_hero_image.alt" />
        </div>
      </div>
      <div
        v-if="data.section_anchor"
        ref="iconScroll"
        class="icon-scroll"
        @click="scrollToHash(data.section_under_hero)">
        <Arrow />
      </div>
    </div>
  </section>
</template>

<script setup>
/* #META
   {
    "NAME": "PageHeroComponent",
    "CLASSNAME": "hero",
    "URL": "http://local.bicara.com/"
   }
  #ENDMETA */
import { ref, onMounted } from 'vue'
import useIntroShown from '@/composables/useIntroShown.js'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import useUtils from '@/composables/useUtils.js'
import Arrow from '../utility/Arrow.vue'
import ArrowCircleButton from '../utility/ArrowCircleButton.vue'
import SplitType from 'split-type'
import locoScroll from '../../assets/js/LenisScroll'

const scrollToHash = (section) => {
  const el = document.getElementById(section)
  if (el) {
    locoScroll.scrollTo(`#${section}`, { immediate: false, duration: 1.2 })
  }
}

gsap.registerPlugin(ScrollTrigger)

const props = defineProps(['data'])

const { genUUID } = useUtils()
const headlineId = ref(`headline-${genUUID()}`)
const buttonId = ref(`button-${genUUID()}`)
const heroLoaded = ref(false)
const pageHeroImg = ref(null)
const trigger = ref(null)

const { introShown } = useIntroShown()

const hasIntroShown = () => {
  if (introShown.value) {
    new SplitType(`#${headlineId.value} h1`, {
      types: 'lines, words',
      tagName: 'span',
    })

    gsap.to(trigger.value, {
      duration: 1,
      opacity: 1,
      ease: 'power1.inOut',
    })

    gsap.to(pageHeroImg.value, {
      duration: 1,
      opacity: 1,
      ease: 'power1.inOut',
    })

    gsap.fromTo(
      `#${headlineId.value} h1 .word`,
      {
        y: '102%',
      },
      {
        delay: 0,
        duration: 1,
        y: 0,
        ease: 'power1.out',
        stagger: 0.1,
      }
    )
    gsap.fromTo(
      `#${headlineId.value} h1 .word`,
      {
        opacity: 0.3,
      },
      {
        delay: 0.8,
        opacity: 1,
        stagger: 0.1,
      }
    )

    gsap.fromTo(
      `#${headlineId.value} p`,
      {
        opacity: 0,
      },
      {
        delay: 1.5,
        opacity: 1,
        duration: 1,
        ease: 'power1.inOut',
      }
    )
    if (props.data.show_button) {
      gsap.fromTo(
        `#${buttonId.value}`,
        {
          opacity: 0,
        },
        {
          delay: 2,
          opacity: 1,
          duration: 1,
          ease: 'power1.inOut',
        }
      )
    }
  } else {
    setTimeout(() => {
      hasIntroShown()
    }, 100)
  }
}

onMounted(() => {
  gsap.set(pageHeroImg.value, { opacity: 0 })
  gsap.set(trigger.value, { opacity: 0 })
  pageHeroImg.value.onload = () => {
    hasIntroShown()
    heroLoaded.value = true
  }
})
</script>

<style lang="scss">
.page-hero {
  @apply relative mb-11 px-xs lg:mb-14 lg:mt-6 lg:px-6 xl:px-7;

  .container {
    @apply xl:px-6;
  }

  &.no-button {
    @apply mb-0;
  }

  .background {
    @apply absolute -bottom-14 left-0 right-0 z-2 h-[200px] w-screen lg:h-[120px];
  }

  .wrapper {
    @apply z-2 flex min-h-[80vh] flex-col overflow-visible rounded-2xl bg-bicara-turquoise bg-menu-background px-lg py-12 lg:justify-end lg:px-8 xl:pl-16 xl:pr-10;

    .hero-content-wrapper {
      @apply flex items-center justify-center pt-0 lg:items-end lg:justify-start;

      .hero-content {
        @apply relative z-5 w-full lg:w-[55%] lg:pb-16 lg:pr-1;

        .button-area {
          .link {
            @apply pl-[20px] pr-[80px] lg:pl-lg lg:pr-[100px];
          }
        }

        .headline {
          &.big-title {
            h1 {
              @apply header-1 md:h-[103px];
            }
          }

          h1 {
            @apply header-2 mb-10 leading-[1.06] text-bicara-dark-blue lg:leading-none;

            .line {
              @apply h-auto overflow-hidden md:h-[83px];
              .word {
                @apply leading-[1.06] lg:leading-none;
              }
            }
            strong {
              @apply font-light text-bicara-blue;
            }
          }

          p {
            @apply body-md text-bicara-dark-blue lg:pr-7;
          }
        }
      }
    }

    .hero-image {
      @apply pointer-events-none mt-10 lg:absolute lg:bottom-0 lg:left-[55%] lg:right-4 lg:top-0 lg:z-4 lg:mt-0 lg:-translate-y-10 xl:right-16;

      img {
        @apply h-full w-full object-cover;
      }
    }
  }

  .icon-scroll {
    @apply absolute bottom-0 left-1/2 z-4 -translate-x-1/2 translate-y-1/2;

    .arrow-circle {
      @apply h-[70px] w-[70px] lg:h-[96px] lg:w-[96px];
    }
  }
}
</style>
